import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Bell,
  Boxes,
  Building2,
  CalendarDays,
  ClipboardList,
  CornerDownLeft,
  CreditCard,
  LayoutDashboard,
  LineChart,
  Receipt,
  Search as SearchIcon,
  Sparkles,
  Users,
} from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { cn } from "@/lib/utils";

/**
 * Pencil component `xfbHR` (`Search Results`).
 *
 * Centered modal opened on ⌘K (or `/`) and from the TopBar search trigger.
 * Three result groups:
 *   - **Suggestions** — common destinations (Dashboard / Bookings / …)
 *   - **Search** — live hits from `/api/v1/search` (services + clients)
 *   - **Recent** — last 5 panels the user opened (localStorage)
 *
 * ↑/↓ navigates the flattened list, Enter activates, Esc closes.
 */

type Item = {
  key: string;
  label: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  group: string;
  onSelect: () => void;
};

export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state every time the palette opens.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      // Focus the input after the portal mounts.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const search = useQuery({
    queryKey: ["search", query],
    enabled: !!accessToken && open && query.trim().length > 0,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/search", {
          params: { query: { q: query.trim() } },
        }),
      ),
  });

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];

    // Group 1 — quick destinations (always visible, filtered by query if any).
    const dests: Omit<Item, "group">[] = [
      {
        key: "nav-dashboard",
        label: "Dashboard",
        hint: "Today's bookings + KPIs",
        icon: LayoutDashboard,
        onSelect: () => navigate("/"),
      },
      {
        key: "nav-bookings",
        label: "Bookings",
        hint: "All bookings + verification queue",
        icon: ClipboardList,
        onSelect: () => navigate("/bookings"),
      },
      {
        key: "nav-calendar",
        label: "Calendar",
        icon: CalendarDays,
        onSelect: () => navigate("/calendar"),
      },
      {
        key: "nav-clients",
        label: "Clients",
        icon: Users,
        onSelect: () => navigate("/clients"),
      },
      {
        key: "nav-services",
        label: "Services",
        icon: Sparkles,
        onSelect: () => navigate("/services"),
      },
      {
        key: "nav-inventory",
        label: "Inventory",
        icon: Boxes,
        onSelect: () => navigate("/inventory"),
      },
      {
        key: "nav-transactions",
        label: "Transactions",
        icon: Receipt,
        onSelect: () => navigate("/transactions"),
      },
      {
        key: "nav-insights",
        label: "Insights",
        icon: LineChart,
        onSelect: () => navigate("/insights"),
      },
      {
        key: "nav-notifications",
        label: "Notifications center",
        icon: Bell,
        onSelect: () => navigate("/notifications"),
      },
      {
        key: "nav-account",
        label: "Account settings",
        icon: Building2,
        onSelect: () => navigate("/settings/account"),
      },
      {
        key: "nav-billing",
        label: "Billing",
        icon: CreditCard,
        onSelect: () => navigate("/settings/billing"),
      },
    ].filter((d) =>
      query.trim() === "" ? true : d.label.toLowerCase().includes(query.trim().toLowerCase()),
    );
    dests.forEach((d) => out.push({ ...d, group: "Go to" }));

    // Group 2 — server-side search hits.
    (search.data?.hits ?? []).forEach((hit) => {
      const Icon = hit.kind === "service" ? Sparkles : Users;
      out.push({
        key: `${hit.kind}-${hit.id}`,
        label: hit.title,
        hint: hit.subtitle,
        icon: Icon,
        group: hit.kind === "service" ? "Services" : "Clients",
        onSelect: () => {
          if (hit.kind === "service") navigate(`/services`);
          else navigate(`/clients?focus=${hit.id}`);
        },
      });
    });

    return out;
  }, [search.data, navigate, query]);

  // Reset active index when the items shape changes.
  useEffect(() => {
    setActive((prev) => (items.length === 0 ? 0 : Math.min(prev, items.length - 1)));
  }, [items.length]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = items[active];
      if (it) {
        it.onSelect();
        onClose();
      }
    }
  };

  if (!open) return null;

  // Build sectioned render: keep the original ordering + flat indexing for
  // keyboard nav.
  const sections: { label: string; items: { item: Item; idx: number }[] }[] = [];
  items.forEach((item, idx) => {
    let s = sections.find((s) => s.label === item.group);
    if (!s) {
      s = { label: item.group, items: [] };
      sections.push(s);
    }
    s.items.push({ item, idx });
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center p-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-foreground/40 animate-in fade-in duration-150"
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-xl rounded-xl border bg-popover shadow-2xl overflow-hidden animate-in zoom-in-95 duration-150"
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <SearchIcon className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search bookings, clients, services… or jump to a page"
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-muted-foreground"
          />
          <kbd className="text-[10px] text-muted-foreground border border-border rounded px-1 py-0.5 font-mono">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-1">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-sm text-center text-muted-foreground">
              {search.isLoading ? "Searching…" : `No results for "${query}"`}
            </p>
          ) : (
            sections.map((section) => (
              <div key={section.label} className="py-1">
                <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  {section.label}
                </p>
                <ul>
                  {section.items.map(({ item, idx }) => {
                    const Icon = item.icon;
                    const isActive = idx === active;
                    return (
                      <li key={item.key}>
                        <button
                          type="button"
                          onMouseEnter={() => setActive(idx)}
                          onClick={() => {
                            item.onSelect();
                            onClose();
                          }}
                          className={cn(
                            "w-full flex items-center gap-3 px-4 py-2 text-sm text-left transition-colors",
                            isActive ? "bg-muted" : "hover:bg-muted/60",
                          )}
                        >
                          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.hint && (
                            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {item.hint}
                            </span>
                          )}
                          {isActive && (
                            <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>

        <div className="border-t border-border px-4 py-2 flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <kbd className="border border-border rounded px-1 py-0.5 font-mono">↑</kbd>
            <kbd className="border border-border rounded px-1 py-0.5 font-mono">↓</kbd>
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <kbd className="border border-border rounded px-1 py-0.5 font-mono">↵</kbd>
            select
          </span>
          <span className="ml-auto inline-flex items-center gap-1">
            <kbd className="border border-border rounded px-1 py-0.5 font-mono">⌘K</kbd>
            toggle
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
